import React, { useMemo } from "react";
import { AlertTriangle, ArrowRight, Clock3, ShieldAlert, User2 } from "lucide-react";
import { useChainStore } from "../../data/chainStore";

const _ms = (v) => (typeof v === "number" ? v : Date.parse(v || ""));

const STAGE_LABEL = {
  offer: "Offer Accepted", aml: "AML / ID", memo: "Memorandum",
  solicitors: "Solicitors Instructed", mortgage_app: "Mortgage Submitted",
  survey: "Survey Booked", searches: "Searches Ordered", enquiries: "Enquiries Raised",
  mortgage_offer: "Mortgage Offer", exchange_ready: "Exchange Ready",
  exchanged: "Exchanged", completed: "Completed",
};

// Stage-driven default "waiting on" actor.
const STAGE_OWNER = {
  offer: "Buyer", aml: "Buyer", memo: "Seller's solicitor",
  solicitors: "Seller's solicitor", mortgage_app: "Buyer's broker",
  survey: "Buyer", searches: "Buyer's solicitor", enquiries: "Seller's solicitor",
  mortgage_offer: "Buyer's broker", exchange_ready: "Buyer + seller solicitors",
  exchanged: "Both solicitors", completed: "Completion team",
};

// Stage fallback "next critical action" copy.
const STAGE_FALLBACK = {
  offer: "Issue memorandum of sale",
  aml: "Collect buyer's AML / ID and proof of funds",
  memo: "Confirm both solicitors instructed",
  solicitors: "Submit mortgage application",
  mortgage_app: "Order local searches",
  survey: "Awaiting survey results",
  searches: "Raise enquiries on returned searches",
  enquiries: "Reply to outstanding enquiries",
  mortgage_offer: "Approve final paperwork",
  exchange_ready: "Agree exchange date with all parties",
  exchanged: "Confirm completion date",
  completed: "Move to handover",
};

const consequenceFor = (deal, days) => {
  const target = deal.targetExchange ? Date.parse(deal.targetExchange) : null;
  if (target && Number.isFinite(target) && days > 3) {
    const daysToTarget = Math.ceil((target - Date.now()) / 86400000);
    if (daysToTarget <= 14) return "Exchange target at risk";
  }
  if (days >= 5) return "Chain health at risk";
  if (days >= 3) return "Delay accumulating";
  return "Routine — keep momentum";
};

export const NextCriticalActionCard = ({ deal, openTasks = [] }) => {
  const { findContact } = useChainStore();

  const card = useMemo(() => {
    const now = Date.now();
    const enrich = (t) => {
      const due = _ms(t.due_at);
      const overdue = Number.isFinite(due) && due < now;
      const days = overdue ? Math.floor((now - due) / 86400000) : 0;
      const owner = findContact(t.assigned_to)?.name
        || STAGE_OWNER[deal.stage] || "Unassigned";
      return { ...t, overdue, daysOverdue: days, owner };
    };

    if (!openTasks.length) {
      // Fallback to stage prompt
      return {
        title: STAGE_FALLBACK[deal.stage] || "Move the deal forward",
        sub: `Linked to stage · ${STAGE_LABEL[deal.stage] || deal.stage}`,
        owner: STAGE_OWNER[deal.stage] || "Unassigned",
        ownerKind: "Stage",
        days: 0,
        overdue: false,
        consequence: "Routine — keep momentum",
        stage: STAGE_LABEL[deal.stage] || deal.stage,
        synthetic: true,
      };
    }

    const sorted = openTasks
      .map(enrich)
      .sort((a, b) => {
        if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
        const am = _ms(a.due_at), bm = _ms(b.due_at);
        return (Number.isFinite(am) ? am : Infinity) - (Number.isFinite(bm) ? bm : Infinity);
      });

    const top = sorted[0];
    return {
      title: top.title,
      sub: top.kickoff_stage_hint
        ? `Linked to stage · ${STAGE_LABEL[top.kickoff_stage_hint] || top.kickoff_stage_hint}`
        : `Linked to stage · ${STAGE_LABEL[deal.stage] || deal.stage}`,
      owner: top.owner,
      ownerKind: "Waiting on",
      days: top.daysOverdue,
      overdue: top.overdue,
      consequence: consequenceFor(deal, top.daysOverdue),
      stage: STAGE_LABEL[top.kickoff_stage_hint] || STAGE_LABEL[deal.stage] || deal.stage,
      synthetic: false,
    };
  }, [openTasks, deal, findContact]);

  const overdueCls = card.overdue
    ? "from-rose-50 to-rose-50/40 border-rose-200"
    : "from-amber-50 to-amber-50/40 border-amber-200";

  return (
    <section
      className={`relative rounded-2xl border bg-gradient-to-br ${overdueCls} overflow-hidden`}
      data-testid="next-critical-action"
    >
      {/* Side accent bar */}
      <span
        aria-hidden="true"
        className={`absolute left-0 top-0 bottom-0 w-1 ${card.overdue ? "bg-rose-500" : "bg-amber-500"}`}
      />

      <div className="p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5 items-start">
        {/* Title / waiting on / consequence */}
        <div className="min-w-0">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 inline-flex items-center gap-1.5 mb-2">
            <span className={`w-1.5 h-1.5 rounded-full ${card.overdue ? "bg-rose-500 cf-pulse" : "bg-amber-500"}`} />
            Next critical action
          </p>
          <h2
            className="font-display text-[22px] lg:text-[26px] font-bold tracking-tight text-slate-900 leading-tight"
            data-testid="next-critical-action-title"
          >
            {card.title}
          </h2>
          <p className="text-[12.5px] text-slate-600 mt-1.5">{card.sub}</p>

          {/* Operational consequence */}
          <p className="text-[11.5px] mt-3 inline-flex items-center gap-1.5">
            <span className="font-bold uppercase tracking-[0.16em] text-[9.5px] text-slate-500">Consequence</span>
            <span className={card.overdue ? "font-bold text-rose-700" : "font-bold text-amber-800"} data-testid="next-critical-action-consequence">
              {card.consequence}
            </span>
          </p>
        </div>

        {/* Waiting On panel */}
        <div className="rounded-xl bg-white/70 backdrop-blur-sm border border-white/80 p-4 ring-1 ring-slate-100">
          <p className="text-[9.5px] font-bold uppercase tracking-[0.20em] text-slate-500">{card.ownerKind}</p>
          <p
            className="font-display text-[18px] font-bold tracking-tight text-slate-900 mt-1 leading-tight inline-flex items-center gap-2"
            data-testid="next-critical-action-owner"
          >
            <User2 size={15} className="text-indigo-600" />
            {card.owner}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-slate-500">Stage</p>
              <p className="font-semibold text-slate-800 truncate">{card.stage}</p>
            </div>
            <div>
              <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-slate-500">Time</p>
              <p className={`font-semibold ${card.overdue ? "text-rose-700" : "text-slate-800"}`}>
                {card.overdue
                  ? `${card.days}d waiting`
                  : card.synthetic ? "On track" : "On track"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NextCriticalActionCard;
